/**
 * NodeRegistry — Global registry of available node definitions.
 *
 * Node packs register their definitions here. The editor palette,
 * compiler, and executor all look up node types through this registry.
 *
 * Extends the generic {@link BaseRegistry} (@engine/graph) — which provides
 * Map storage, the category + keyword indexes, and scored `search()` — and
 * adds only the visual-script specifics: event/pure-node filters and port
 * resolution.
 */

import { BaseRegistry } from '@engine/graph';
import type { NodeBehaviour, NodeDefinition, PortDefinition } from '../types';

// ============================================================================
// Registry
// ============================================================================

export class NodeRegistry extends BaseRegistry<NodeBehaviour> {
  /**
   * Get all event nodes (lifecycle entry points).
   */
  getEventNodes(): readonly NodeBehaviour[] {
    return this.getAll().filter((n) => n.isEvent);
  }

  /**
   * Get all pure nodes (no side effects, no flow ports).
   */
  getPureNodes(): readonly NodeBehaviour[] {
    return this.getAll().filter((n) => n.isPure);
  }

  // --------------------------------------------------------------------------
  // Port resolution
  // --------------------------------------------------------------------------

  /**
   * Resolve a port definition (input or output) for a given node type and port ID.
   */
  resolvePort(nodeTypeId: string, portId: string): PortDefinition | undefined {
    const def = this.get(nodeTypeId);
    if (!def) return undefined;
    return (def.inputs as PortDefinition[]).find((p) => p.id === portId)
        ?? (def.outputs as PortDefinition[]).find((p) => p.id === portId);
  }

  /**
   * Get the node definition's input port by ID.
   */
  resolveInputPort(nodeTypeId: string, portId: string): PortDefinition | undefined {
    return this.get(nodeTypeId)?.inputs.find((p) => p.id === portId);
  }

  /**
   * Get the node definition's output port by ID.
   */
  resolveOutputPort(nodeTypeId: string, portId: string): PortDefinition | undefined {
    return this.get(nodeTypeId)?.outputs.find((p) => p.id === portId);
  }
}

// ============================================================================
// Singleton for convenience (optional — consumers can create their own)
// ============================================================================

let _defaultRegistry: NodeRegistry | null = null;

/**
 * Get the default global node registry.
 */
export function getDefaultRegistry(): NodeRegistry {
  if (!_defaultRegistry) _defaultRegistry = new NodeRegistry();
  return _defaultRegistry;
}

// ============================================================================
// Builder helpers
// ============================================================================

/**
 * Helper to define an input port concisely.
 */
export function input(id: string, name: string, type: NodeDefinition['inputs'][number]['type'], defaultValue?: unknown): PortDefinition {
  return { id, name, type, direction: 'input', defaultValue };
}

/**
 * Helper to define an output port concisely.
 */
export function output(id: string, name: string, type: NodeDefinition['outputs'][number]['type']): PortDefinition {
  return { id, name, type, direction: 'output' };
}

/**
 * Helper: flow input port.
 */
export function flowIn(id = 'exec_in', name = ''): PortDefinition {
  return { id, name, type: 'flow', direction: 'input' };
}

/**
 * Helper: flow output port.
 */
export function flowOut(id = 'exec_out', name = ''): PortDefinition {
  return { id, name, type: 'flow', direction: 'output' };
}
